import { fitImportedImage, imageImportBounds } from "./image-sizing";
import { imagePlacement } from "./sheet-operations";
import { AssetRecordType, createShapeId } from "@tldraw/tlschema";
import type { Editor, VecLike } from "./canvas";
import type { TLAssetId, TLImageAsset, TLImageShape } from "@tldraw/tlschema";
import { IMAGE_TYPES, MAX_IMAGE_BYTES, taskQueue } from "../shared/images";
import { notify } from "./api";
import type { ImageOutbox, UploadReceipt } from "./image-outbox";
import { ImagePreviews } from "./image-previews";

export type Preview = { w: number; h: number; blob: Blob };
export type ImageJob = {
  id: TLAssetId;
  name: string;
  status:
    | "Queued"
    | "Saving on this device"
    | "Preparing preview"
    | "Uploading"
    | "Failed";
  saved?: boolean;
  missingCard?: boolean;
  missingSource?: boolean;
  error?: string;
};
type PendingImage = {
  job: ImageJob;
  file?: File;
  ready: boolean;
  receipt?: UploadReceipt;
};
export const imageImports = new WeakMap<Editor, ImageImports>();
export class ImageImports {
  private pending = new Map<TLAssetId, PendingImage>();
  private listeners = new Set<() => void>();
  private snapshot: ImageJob[] = [];
  private prepare = taskQueue(1);
  private upload = taskQueue(2);
  private process = taskQueue(2);
  private save = taskQueue(1);
  private cancelPreviews = new Set<() => void>();
  private previewWorker?: Worker;
  private disposed = false;
  private restored = false;
  constructor(
    private editor: Editor,
    private previewReader?: (file: File) => Promise<Preview>,
    private outbox?: ImageOutbox,
    private previews = new ImagePreviews(),
    private options: { userId?: string; maxBytes?: number } = {},
  ) {
    imageImports.set(editor, this);
  }
  // The caller starts this only after the server's board snapshot has arrived.
  async restore() {
    if (
      this.restored ||
      this.disposed ||
      this.editor.getIsReadonly() ||
      !this.outbox
    )
      return;
    this.restored = true;
    try {
      const savedImages = await this.outbox.list();
      for (const saved of savedImages) {
        if (this.disposed || this.editor.getIsReadonly()) return;
        const asset = this.editor.getAsset<TLImageAsset>(saved.id);
        if (asset?.props.src) {
          // Remounts may see optimistic state before the server acknowledges it.
          // Release large files, but retain a small receipt for later repair.
          if (!saved.receipt)
            await this.outbox.put({
              id: saved.id,
              name: saved.name,
              receipt: {
                src: asset.props.src,
                meta: asset.meta,
                w: asset.props.w,
                h: asset.props.h,
              },
            });
          continue;
        }
        if (!asset) {
          // The card may never have synced, or may have been deleted deliberately.
          // Preserve the source, but require an explicit action to add it back.
          this.pending.set(saved.id, {
            receipt: saved.receipt,
            ready: false,
            job: {
              id: saved.id,
              name: saved.name,
              status: "Failed",
              saved: true,
              missingCard: true,
              error:
                "This image card is no longer on the board. Add it back or forget the saved upload.",
            },
          });
          continue;
        }
        if (saved.receipt) {
          this.applyReceipt(saved.id, saved.receipt);
          // Another refresh may still need to repair an unacknowledged update.
          continue;
        }
        if (!saved.hasFile || this.pending.has(saved.id)) continue;
        const item: PendingImage = {
          ready: false,
          job: {
            id: saved.id,
            name: saved.name,
            status: "Queued",
            saved: true,
          },
        };
        this.pending.set(saved.id, item);
        void this.run(item);
      }
      if (this.options.userId) {
        const savedIds = new Set(savedImages.map((item) => item.id));
        for (const asset of this.editor.getAssets()) {
          if (
            asset.type !== "image" ||
            asset.props.src ||
            savedIds.has(asset.id) ||
            asset.meta.imageUploadOwner !== this.options.userId
          )
            continue;
          this.pending.set(asset.id, {
            ready: false,
            job: {
              id: asset.id,
              name: asset.props.name,
              status: "Failed",
              missingSource: true,
              error:
                "This original was not saved before the page closed. Choose it again to keep this card and its position.",
            },
          });
        }
      }
      this.changed();
    } catch {
      if (!this.disposed)
        notify(
          "Cannot read saved uploads on this device. Reopen this board to try again.",
        );
      this.restored = false;
    }
  }
  hasUnsavedFiles = () =>
    [...this.pending.values()].some(
      (item) => !item.job.saved && !item.job.missingSource,
    );
  private active(item: PendingImage) {
    return (
      !this.disposed &&
      this.pending.get(item.job.id) === item &&
      !this.editor.getIsReadonly()
    );
  }
  private applyReceipt(id: TLAssetId, receipt: UploadReceipt) {
    const asset = this.editor.getAsset<TLImageAsset>(id);
    if (!asset || this.disposed || this.editor.getIsReadonly()) return;
    this.editor.run(
      () =>
        this.editor.updateAssets([
          {
            id,
            type: "image",
            props: {
              ...asset.props,
              src: receipt.src,
              w: receipt.w,
              h: receipt.h,
            },
            meta: { ...asset.meta, ...receipt.meta, imageUpload: "done" },
          },
        ]),
      { history: "ignore" },
    );
  }
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  getSnapshot = () => this.snapshot;
  private changed() {
    this.snapshot = [...this.pending.values()].map((p) => ({ ...p.job }));
    this.listeners.forEach((fn) => fn());
  }
  dispose() {
    this.disposed = true;
    this.previews.clear();
    this.cancelPreviews.forEach((cancel) => cancel());
    this.cancelPreviews.clear();
    this.previewWorker?.terminate();
    this.previewWorker = undefined;
    for (const item of this.pending.values()) item.file = undefined;
    this.pending.clear();
    this.listeners.clear();
    imageImports.delete(this.editor);
  }
  pendingUsage() {
    return {
      count: this.pending.size,
      bytes: [...this.pending.values()].reduce(
        (sum, item) => sum + (item.file?.size ?? 0),
        0,
      ),
    };
  }
  add(
    files: File[],
    point?: VecLike,
    placement?: ReturnType<typeof imagePlacement>,
  ) {
    if (this.editor.getIsReadonly() || this.disposed) return [];
    if (files.length + this.pending.size > 50) {
      notify(
        "Wait for pending images to finish, or forget failed uploads, before adding more than 50.",
      );
      return [];
    }
    const retainedBytes = [...this.pending.values()].reduce(
      (sum, item) => sum + (item.file?.size ?? 0),
      0,
    );
    if (
      retainedBytes + files.reduce((sum, file) => sum + file.size, 0) >
      150 * 1024 * 1024
    ) {
      notify(
        "Add up to 150 MB of images at a time. Split these images into smaller batches.",
      );
      return [];
    }
    const accepted = files.filter(
      (file) =>
        IMAGE_TYPES.includes(file.type) &&
        file.size <= (this.options.maxBytes ?? MAX_IMAGE_BYTES),
    );
    if (accepted.length !== files.length)
      notify(
        `Use PNG, JPEG or WebP images up to ${this.options.maxBytes === 20_000_000 ? 20 : 30} MB each. Add PDFs with Upload a plan.`,
      );
    const { center, parentId } =
      placement ?? imagePlacement(this.editor, point);
    const bounds = imageImportBounds(this.editor, parentId);
    const gap = Math.min(24, Math.min(bounds.w, bounds.h) / 10);
    const columns = Math.min(5, Math.ceil(Math.sqrt(accepted.length)));
    const ids: TLImageShape["id"][] = [];
    this.editor.markHistoryStoppingPoint("Add inspiration images");
    this.editor.run(() => {
      accepted.forEach((file, index) => {
        const id = AssetRecordType.createId(),
          shapeId = createShapeId();
        this.editor.createAssets([
          AssetRecordType.create({
            id,
            type: "image",
            props: {
              src: null,
              name: file.name,
              w: 320,
              h: 240,
              mimeType: file.type,
              isAnimated: false,
            },
            meta: {
              imageUpload: "pending",
              imageInitialWidth: bounds.w,
              imageInitialHeight: bounds.h,
              ...(this.options.userId
                ? { imageUploadOwner: this.options.userId }
                : {}),
            },
          }),
        ]);
        this.editor.createShape<TLImageShape>({
          opacity: 1,
          id: shapeId,
          type: "image",
          parentId,
          x:
            center.x -
            (columns * bounds.w + (columns - 1) * gap) / 2 +
            (index % columns) * (bounds.w + gap),
          y:
            center.y -
            bounds.h / 2 +
            Math.floor(index / columns) * (bounds.h + gap),
          props: { ...bounds, assetId: id, altText: file.name },
        });
        ids.push(shapeId);
        const pending: PendingImage = {
          file,
          ready: false,
          job: { id, name: file.name, status: "Queued" },
        };
        this.pending.set(id, pending);
      });
      this.editor.select(...ids);
    });
    this.changed();
    for (const item of this.pending.values())
      if (item.job.status === "Queued") void this.run(item);
    return ids;
  }
  reselect(id: TLAssetId, file: File) {
    const item = this.pending.get(id);
    if (!item?.job.missingSource || !this.active(item)) return;
    if (
      !IMAGE_TYPES.includes(file.type) ||
      file.size > (this.options.maxBytes ?? MAX_IMAGE_BYTES)
    ) {
      notify("Choose a supported image within the upload size limit.");
      return;
    }
    const retained = [...this.pending.values()].reduce(
      (n, item) => n + (item.file?.size ?? 0),
      0,
    );
    if (retained + file.size > 150 * 1024 * 1024) {
      notify("Wait for your other originals to save first.");
      return;
    }
    item.file = file;
    item.job.saved = false;
    item.job.missingSource = false;
    item.job.error = undefined;
    item.job.status = "Queued";
    item.ready = false;
    this.changed();
    void this.run(item);
  }
  retry(id: TLAssetId) {
    const item = this.pending.get(id);
    if (item?.job.status === "Failed" && this.active(item)) {
      if (!this.editor.getAsset(id)) {
        const center = this.editor.getViewportPageBounds().center;
        const w = item.receipt?.w ?? 320,
          h = item.receipt?.h ?? 240;
        const bounds = imageImportBounds(
          this.editor,
          this.editor.getCurrentPageId(),
        );
        const size = fitImportedImage({ w, h }, bounds);
        this.editor.markHistoryStoppingPoint("Recover image");
        this.editor.createAssets([
          AssetRecordType.create({
            id,
            type: "image",
            props: {
              src: null,
              name: item.job.name,
              w,
              h,
              mimeType: item.file?.type || "image/jpeg",
              isAnimated: false,
            },
            meta: {
              imageUpload: "pending",
              imageInitialWidth: size.w,
              imageInitialHeight: size.h,
              ...(this.options.userId
                ? { imageUploadOwner: this.options.userId }
                : {}),
            },
          }),
        ]);
        this.editor.createShape<TLImageShape>({
          opacity: 1,
          id: createShapeId(),
          type: "image",
          parentId: this.editor.getCurrentPageId(),
          x: center.x - size.w / 2,
          y: center.y - size.h / 2,
          props: {
            w: size.w,
            h: size.h,
            assetId: id,
            altText: item.job.name,
          },
        });
      }
      item.job.missingCard = false;
      item.job.status = "Queued";
      item.job.error = undefined;
      this.changed();
      void this.run(item);
    }
  }
  dismiss(id: TLAssetId) {
    this.previews.remove(id);
    this.pending.delete(id);
    void this.outbox
      ?.remove(id)
      .catch(() =>
        notify(
          "Could not forget this saved upload. It may reappear when you reopen the board.",
        ),
      );
    this.changed();
  }
  private async preview(file: File): Promise<Preview> {
    const worker = (this.previewWorker ??= new Worker(
      new URL("./image-preview.worker.ts", import.meta.url),
      { type: "module" },
    ));
    let cancel = () => {};
    try {
      return await new Promise((resolve, reject) => {
        const finish = (error?: Error, value?: Preview) => {
          clearTimeout(timeout);
          worker.onmessage = null;
          worker.onerror = null;
          if (error) {
            worker.terminate();
            this.previewWorker = undefined;
          }
          error ? reject(error) : resolve(value!);
        };
        const timeout = setTimeout(() => {
          finish(
            new Error("Image preview took too long. Try a smaller image."),
          );
        }, 30_000);
        cancel = () => finish(new Error("Image preview cancelled."));
        this.cancelPreviews.add(cancel);
        worker.onmessage = (event) => {
          event.data.error
            ? finish(new Error(event.data.error))
            : finish(undefined, event.data);
        };
        worker.onerror = () => {
          finish(
            new Error(
              "Cannot prepare this image. Try another PNG, JPEG or WebP.",
            ),
          );
        };
        worker.postMessage(file);
      });
    } finally {
      this.cancelPreviews.delete(cancel);
    }
  }
  private async run(item: PendingImage) {
    // Mark synchronously, so a second paste cannot schedule the same queued file twice.
    item.job.status = item.job.saved
      ? "Preparing preview"
      : "Saving on this device";
    const id = item.job.id;
    try {
      if (item.receipt) {
        this.applyReceipt(id, item.receipt);
        this.pending.delete(id);
        return;
      }
      if (this.outbox && !item.job.saved) {
        try {
          await this.save(async () => {
            if (!this.active(item)) return;
            await this.outbox!.put({
              id,
              name: item.job.name,
              file: item.file,
            });
            item.file = undefined;
          });
          if (!this.active(item)) return;
          item.job.saved = true;
          item.job.status = "Preparing preview";
        } catch {
          throw new Error(
            "Not saved on this device. Keep this tab open, free some browser storage, then retry.",
          );
        }
      }
      if (!this.active(item)) return;
      await this.process(async () => {
        if (!this.active(item)) return;
        const savedFile = item.file ?? (await this.outbox?.get(id))?.file;
        if (!savedFile)
          throw new Error(
            "This saved file is unavailable. Add the original image again.",
          );
        const file = new File([savedFile], item.job.name, {
          type: savedFile.type,
        });
        if (!item.ready)
          await this.prepare(async () => {
            if (!this.active(item) || !this.editor.getAsset(id)) return;
            item.job.status = "Preparing preview";
            this.changed();
            const preview = await (this.previewReader
              ? this.previewReader(file)
              : this.preview(file));
            if (!this.active(item) || !this.editor.getAsset(id)) return;
            this.previews.set(id, preview.blob);
            this.editor.run(
              () => {
                this.editor.updateAssets([
                  {
                    id,
                    type: "image",
                    props: {
                      ...this.editor.getAsset<TLImageAsset>(id)!.props,
                      w: preview.w,
                      h: preview.h,
                    },
                  },
                ]);
                const meta = this.editor.getAsset<TLImageAsset>(id)!.meta;
                // Legacy pending cards used a 320 × 240 placeholder.
                const initial = {
                  w: Number(meta.imageInitialWidth ?? 320),
                  h: Number(meta.imageInitialHeight ?? 240),
                };
                const size = fitImportedImage(preview, initial);
                this.editor.updateShapes<TLImageShape>(
                  this.editor
                    .getCurrentPageShapes()
                    .filter(
                      (s): s is TLImageShape =>
                        s.type === "image" &&
                        s.props.assetId === id &&
                        s.props.w === initial.w &&
                        s.props.h === initial.h,
                    )
                    .map((s) => ({
                      id: s.id,
                      type: "image",
                      props: size,
                    })),
                );
              },
              { history: "ignore" },
            );
            item.ready = true;
          });
        await this.upload(async () => {
          if (!this.active(item) || !this.editor.getAsset(id)) return;
          if (this.editor.getAsset<TLImageAsset>(id)?.props.src) return;
          item.job.status = "Uploading";
          this.changed();
          const asset = this.editor.getAsset<TLImageAsset>(id)!;
          const result = await this.editor.uploadAsset(asset, file);
          if (!this.active(item) || !this.editor.getAsset(id)) return;
          const receipt: UploadReceipt = {
            src: result.src,
            meta: result.meta ?? {},
            w: asset.props.w,
            h: asset.props.h,
          };
          // A reload between HTTP success and sync acknowledgement must not strand the card.
          await this.outbox?.put({ id, name: item.job.name, receipt });
          if (!this.active(item)) return;
          this.applyReceipt(id, receipt);
          this.previews.remove(id);
        });
      });
      if (this.active(item)) this.pending.delete(id);
    } catch (error) {
      if (!this.active(item)) return;
      item.job.status = "Failed";
      item.job.error = (error as Error).message;
      if (this.editor.getAsset(id))
        this.editor.run(
          () =>
            this.editor.updateAssets([
              { id, type: "image", meta: { imageUpload: "failed" } },
            ]),
          { history: "ignore" },
        );
    } finally {
      if (!this.disposed) this.changed();
    }
  }
}
